import { useMemo } from 'react';
import type { AnalizaMatrix, AnalizaHazard, AnalizaRiskClassDef } from '@/api/endpoints';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Icon } from '@/components/Icon';

/**
 * Wizualizacja trójparametrycznej macierzy ryzyka R = P × C × V (Tchórzewska-Cieślak 2017):
 * (1) pasek rozkładu klas, (2) plasterki P×V per wartość C (facet trzeciej osi),
 * (3) tabela ocenionych zagrożeń z edycją P/C/V i barwioną komórką klasy (zał. tab. nr 4).
 */
export function PcvMatrix({ matrix, riskClasses, readOnly, onUpdate, onDelete }: {
  matrix: AnalizaMatrix;
  riskClasses: AnalizaRiskClassDef[];
  readOnly?: boolean;
  onUpdate?: (hid: number, patch: Record<string, unknown>) => void;
  onDelete?: (hid: number) => void;
}) {
  const classFor = useMemo(() => (score: number): AnalizaRiskClassDef | undefined =>
    riskClasses.find(rc => score >= rc.min && score <= rc.max)
    ?? riskClasses[riskClasses.length - 1], [riskClasses]);

  const total = Object.values(matrix.distribution).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-5">
      {/* Rozkład klas */}
      <div>
        <div className="text-[12px] font-semibold text-ink-600 mb-1.5">Rozkład klas ryzyka</div>
        <div className="flex h-6 w-full overflow-hidden rounded border border-ink-200">
          {riskClasses.map(rc => {
            const n = matrix.distribution[rc.name] ?? 0;
            const pct = total ? (n / total) * 100 : 0;
            return (
              <div key={rc.name} title={`${rc.label}: ${n}`}
                   style={{ width: `${pct}%`, background: rc.hex }}
                   className="flex items-center justify-center text-[11px] font-semibold text-white">
                {n > 0 && pct > 8 ? n : ''}
              </div>
            );
          })}
          {total === 0 && <div className="flex-1 grid place-items-center text-[11px] text-ink-400">brak zagrożeń</div>}
        </div>
        <div className="flex gap-4 mt-1.5 flex-wrap">
          {riskClasses.map(rc => (
            <span key={rc.name} className="inline-flex items-center gap-1.5 text-[11px] text-ink-600">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: rc.hex }}/>
              {rc.label} <span className="text-ink-400">(R {rc.min}–{rc.max})</span>: {matrix.distribution[rc.name] ?? 0}
            </span>
          ))}
        </div>
      </div>

      {/* Plasterki P×V per C */}
      <div>
        <div className="text-[12px] font-semibold text-ink-600 mb-1.5">
          Plasterki P×V wg dotkliwości C <span className="text-ink-400 font-normal">(liczność zagrożeń w komórce)</span>
        </div>
        <div className="flex gap-3 flex-wrap">
          {matrix.slices.map(sl => (
            <div key={sl.c} className="text-center">
              <div className="text-[11px] text-ink-500 mb-0.5">C = {sl.c}</div>
              <div className="inline-grid" style={{ gridTemplateColumns: 'repeat(5, 20px)', gridTemplateRows: 'repeat(5, 18px)' }}>
                {[5, 4, 3, 2, 1].map(v =>
                  [1, 2, 3, 4, 5].map(p => {
                    const count = sl.grid[p - 1]?.[v - 1] ?? 0;
                    const rc = classFor(p * sl.c * v);
                    return (
                      <div key={`${p}-${v}`}
                           title={`P=${p}, C=${sl.c}, V=${v} → R=${p * sl.c * v}`}
                           style={{ background: rc?.hex, opacity: count ? 1 : 0.22 }}
                           className="grid place-items-center text-[9px] font-semibold text-white border border-white/40">
                        {count || ''}
                      </div>
                    );
                  })
                )}
              </div>
              <div className="text-[9px] text-ink-400 mt-0.5">P → · V ↑</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabela zagrożeń (zał. tab. nr 4) */}
      <div className="overflow-x-auto">
        <table className="table-hw text-[12px]">
          <thead>
            <tr>
              <th className="w-8">Lp.</th>
              <th className="w-16">art.130</th>
              <th>Zagrożenie</th>
              <th className="w-14">P</th>
              <th className="w-14">C</th>
              <th className="w-14">V</th>
              <th className="w-12">R</th>
              <th className="w-32">Klasa</th>
              {!readOnly && <th className="w-8"></th>}
            </tr>
          </thead>
          <tbody>
            {matrix.hazards.map((h, i) => (
              <HazardRow key={h.id} h={h} lp={i + 1} readOnly={readOnly}
                         onUpdate={onUpdate} onDelete={onDelete}/>
            ))}
            {matrix.hazards.length === 0 && (
              <tr><td colSpan={readOnly ? 8 : 9} className="text-ink-400 py-3 text-center">
                Brak zagrożeń — zasiej bibliotekę art. 130 lub dodaj ręcznie.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HazardRow({ h, lp, readOnly, onUpdate, onDelete }: {
  h: AnalizaHazard; lp: number; readOnly?: boolean;
  onUpdate?: (hid: number, patch: Record<string, unknown>) => void;
  onDelete?: (hid: number) => void;
}) {
  const sel = (field: 'p' | 'c' | 'v', value: number) => (
    <SearchableSelect value={String(value)} disabled={readOnly}
                      onChange={v => onUpdate?.(h.id, { [field]: Number(v) })} className="btn-sm">
      {[1, 2, 3, 4, 5].map(n => <option key={n} value={String(n)}>{n}</option>)}
    </SearchableSelect>
  );
  return (
    <tr>
      <td className="text-ink-500">{lp}</td>
      <td className="font-mono text-[11px]">{h.art130Point ?? '—'}</td>
      <td>
        <div className="font-medium">{h.title}</div>
        {h.description && <div className="text-[11px] text-ink-400 line-clamp-2">{h.description}</div>}
        {h.aiGenerated && <span className="pill pill-ai text-[10px] mt-0.5">AI</span>}
      </td>
      <td>{sel('p', h.p)}</td>
      <td>{sel('c', h.c)}</td>
      <td>{sel('v', h.v)}</td>
      <td className="font-semibold text-center" style={{ background: h.riskClassHex, color: '#fff' }}>{h.r}</td>
      <td className="font-semibold text-center" style={{ background: h.riskClassHex, color: '#fff' }}>{h.riskClassLabel}</td>
      {!readOnly && (
        <td>
          <button className="text-ink-400 hover:text-red-500" title="Usuń zagrożenie"
                  onClick={() => onDelete?.(h.id)}>
            <Icon name="x" className="w-3.5 h-3.5"/>
          </button>
        </td>
      )}
    </tr>
  );
}
